/**
 * CombustibleLayout — Contenedor del módulo Combustible dentro de Flota.
 * Tabs superiores: Hoy · Cargas · Estaciones.
 * Móvil-first: los pilotos entran desde su celular en la estación de servicio.
 */
import React, { useState, useMemo } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Box, Tabs, Tab, Typography } from '@mui/material';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import TodayIcon from '@mui/icons-material/Today';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import StoreIcon from '@mui/icons-material/Store';
import SpeedIcon from '@mui/icons-material/Speed';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

import HoyPage from './combustible/HoyPage';
import CargasPage from './combustible/CargasPage';
import EstacionesPage from './combustible/EstacionesPage';
import LecturasPage from './combustible/LecturasPage';
import ReportesPage from './combustible/ReportesPage';
import OperacionPage from './combustible/OperacionPage';
import DeudaPage from './combustible/DeudaPage';

function getUserRole() {
  try { return (JSON.parse(localStorage.getItem('user') || '{}').role || '').toLowerCase(); }
  catch { return ''; }
}

const TABS = [
  { path: 'hoy',         label: 'Hoy',        icon: <TodayIcon />,         color: '#0ea5e9', roles: ['piloto', 'operaciones', 'admin', 'superadmin'] },
  { path: 'cargas',      label: 'Cargas',     icon: <ReceiptLongIcon />,   color: '#dc2626', roles: ['piloto', 'operaciones', 'admin', 'superadmin'] },
  { path: 'lecturas',    label: 'Lecturas',   icon: <SpeedIcon />,         color: '#0284c7', roles: ['piloto', 'operaciones', 'admin', 'superadmin'] },
  { path: 'operacion',   label: 'Operación',  icon: <PlayCircleFilledIcon />, color: '#ea580c', roles: ['piloto', 'operaciones', 'admin', 'superadmin'] },
  { path: 'deuda',       label: 'Deuda',      icon: <AccountBalanceIcon />, color: '#16a34a', roles: ['operaciones', 'admin', 'superadmin'] },
  { path: 'reportes',    label: 'Reportes',   icon: <TrendingUpIcon />,    color: '#059669', roles: ['operaciones', 'admin', 'superadmin'] },
  { path: 'estaciones',  label: 'Estaciones', icon: <StoreIcon />,         color: '#7c3aed', roles: ['admin', 'superadmin'] },
];

export default function CombustibleLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const role = getUserRole();
  const visibleTabs = useMemo(() => TABS.filter(t => t.roles.includes(role)), [role]);

  // Detectar tab actual
  const currentIdx = Math.max(0, visibleTabs.findIndex(t =>
    location.pathname.includes(`/flota/combustible/${t.path}`)
  ));

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <LocalGasStationIcon sx={{ fontSize: 32, color: '#dc2626' }} />
        <Box>
          <Typography sx={{ fontSize: { xs: 20, sm: 24 }, fontWeight: 700, color: '#0f172a', lineHeight: 1.1 }}>
            Combustible
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#64748b' }}>
            Cargas · Lecturas de tanque · Reportes
          </Typography>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: '1px solid #e2e8f0', mb: 2 }}>
        <Tabs
          value={currentIdx}
          onChange={(_, idx) => navigate(`/flota/combustible/${visibleTabs[idx].path}`)}
          variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
          sx={{
            minHeight: 44,
            '& .MuiTab-root': { minHeight: 44, textTransform: 'none', fontWeight: 700, fontSize: 13 },
            '& .MuiTabs-indicator': { backgroundColor: '#dc2626', height: 3, borderRadius: 2 },
          }}
        >
          {visibleTabs.map(t => (
            <Tab key={t.path} icon={t.icon} iconPosition="start" label={t.label} />
          ))}
        </Tabs>
      </Box>

      {/* Contenido */}
      <Routes>
        <Route index element={<Navigate to="hoy" replace />} />
        <Route path="hoy"        element={<HoyPage />} />
        <Route path="cargas"     element={<CargasPage />} />
        <Route path="lecturas"   element={<LecturasPage />} />
        <Route path="operacion"  element={<OperacionPage />} />
        <Route path="deuda"      element={<DeudaPage />} />
        <Route path="reportes"   element={<ReportesPage />} />
        <Route path="estaciones" element={<EstacionesPage />} />
      </Routes>
    </Box>
  );
}
